/**
 * POS 商品規格選擇 Modal
 * 有規格商品點擊後彈出，供店員選擇規格與數量後加入購物車
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { formatTwdFromCents } from '@pos/shared/money';
import { formatVariantSnapshot } from '@pos/shared/utils';
import type { Product, ProductVariant } from './ProductCard';

/** 規格維度 key → 顯示標籤（與 formatVariantSnapshot 內建一致） */
const DIM_LABEL: Record<string, string> = {
  size: '尺寸',
  color: '顏色',
  capacity: '容量',
  material: '材質',
};

export interface ProductVariantModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product | null;
  /** 已選規格（依維度 key → 選項值） */
  selectedByDimension: Record<string, string> | null;
  onSelectedByDimensionChange: (
    value:
      | Record<string, string>
      | null
      | ((prev: Record<string, string> | null) => Record<string, string> | null)
  ) => void;
  /** 由 selectedByDimension 對應到的規格（父層計算後傳入） */
  selectedVariant: ProductVariant | null;
  quantity: number;
  onQuantityMinus: () => void;
  onQuantityPlus: () => void;
  onAddToCart: () => void;
  onClose: () => void;
}

/**
 * 規格選擇 Modal：圖片、規格維度選擇、數量、加入購物車
 */
export function ProductVariantModal({
  open,
  onOpenChange,
  product,
  selectedByDimension,
  onSelectedByDimensionChange,
  selectedVariant,
  quantity,
  onQuantityMinus,
  onQuantityPlus,
  onAddToCart,
  onClose,
}: ProductVariantModalProps) {
  if (!product) return null;

  const hasVariants = Boolean(
    product.hasVariants && product.variants && product.variants.length > 0
  );
  const variantsList = product.variants ?? [];
  const dimensionKeys = hasVariants
    ? Array.from(
        new Set(variantsList.flatMap(v => Object.keys(v.combination ?? {})))
      ).sort()
    : [];
  const priceCents = selectedVariant?.unitPriceTwd ?? product.price;
  const canAdd = hasVariants ? !!selectedVariant : true;
  const stepperAtMin = quantity <= 1;
  const stepperAtMax = quantity >= 99;

  const handleAdd = () => {
    onAddToCart();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="sr-only">{product.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* 圖片 */}
          {product.thumbnail ? (
            <div className="aspect-square w-full max-h-48 rounded-lg overflow-hidden bg-gray-100">
              <img
                src={product.thumbnail}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="aspect-square w-full max-h-48 rounded-lg bg-gray-100 flex items-center justify-center">
              <svg
                className="w-12 h-12 text-gray-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          )}

          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {product.name}
            </h3>
            <p className="text-base font-semibold text-blue-600 mt-1">
              {formatTwdFromCents(priceCents)}
            </p>
          </div>

          {/* 規格維度選擇 */}
          {hasVariants && dimensionKeys.length > 0 && (
            <div className="space-y-3">
              {dimensionKeys.map(dimKey => {
                const values = Array.from(
                  new Set(
                    variantsList
                      .map(v => v.combination?.[dimKey])
                      .filter(Boolean) as string[]
                  )
                ).sort();
                return (
                  <div key={dimKey}>
                    <p className="text-sm font-medium text-gray-700 mb-1.5">
                      {DIM_LABEL[dimKey] ?? dimKey}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {values.map(val => {
                        const isSelected =
                          selectedByDimension?.[dimKey] === val;
                        return (
                          <button
                            key={val}
                            type="button"
                            onClick={() =>
                              onSelectedByDimensionChange(prev => ({
                                ...(prev ?? {}),
                                [dimKey]: val,
                              }))
                            }
                            className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                              isSelected
                                ? 'border-blue-600 bg-blue-50 text-blue-700'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            {val}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* 數量與加入購物車 */}
          <div className="flex items-center justify-between gap-4 pt-2 border-t">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onQuantityMinus}
                disabled={stepperAtMin}
                className="h-10 w-10 rounded-full border border-input bg-background shrink-0 flex items-center justify-center text-lg disabled:opacity-50"
              >
                −
              </button>
              <span className="w-8 text-center font-medium">{quantity}</span>
              <button
                type="button"
                onClick={onQuantityPlus}
                disabled={stepperAtMax}
                className="h-10 w-10 rounded-full border border-input bg-background shrink-0 flex items-center justify-center text-lg disabled:opacity-50"
              >
                +
              </button>
            </div>
            <button
              type="button"
              onClick={handleAdd}
              disabled={!canAdd}
              className="flex-1 max-w-[200px] bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-2.5 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              加入購物車
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * 依已選維度找出對應的規格
 */
export function findVariantByDimension(
  variants: ProductVariant[],
  selectedByDimension: Record<string, string> | null
): ProductVariant | null {
  if (!selectedByDimension || Object.keys(selectedByDimension).length === 0)
    return null;
  return (
    variants.find(v => {
      const comb = v.combination ?? {};
      return Object.entries(selectedByDimension).every(
        ([k, val]) => comb[k] === val
      );
    }) ?? null
  );
}

/**
 * 將規格 combination 轉成顯示字串（與 formatVariantSnapshot 一致）
 */
export function variantCombinationToLabel(
  combination: Record<string, string>
): string {
  return formatVariantSnapshot(combination);
}
